import dateFormat from "dateformat";
import {
  buildMetadataTooltipLines,
  hasMeasureMetadata,
} from "./measureFields";

export const METADATA_POINT_STYLE = {
  radius: 5,
  bg: "#8fad15",
  border: "#1c4220",
  borderWidth: 2,
  pointStyle: "rectRot",
};

const DEFAULT_FORMAT_LABEL = (date) =>
  dateFormat(date, "UTC:dd/mm/yy' 'HH:MM");

export const resolveChartMeasureItem = ({
  dataIndex,
  chartLabels,
  data,
  sourceIndices,
  formatLabel = DEFAULT_FORMAT_LABEL,
}) => {
  if (sourceIndices?.[dataIndex] !== undefined) {
    return data?.[sourceIndices[dataIndex]] ?? null;
  }

  const label = chartLabels?.[dataIndex];
  if (!label || !Array.isArray(data)) return null;

  return (
    data.find(
      (entry) =>
        entry?.date &&
        formatLabel(entry.date) === label &&
        hasMeasureMetadata(entry)
    ) ??
    data.find(
      (entry) => entry?.date && formatLabel(entry.date) === label
    ) ??
    null
  );
};

export const buildChartPointStyleProps = ({
  chartLabels,
  data,
  sourceIndices,
  getSelectedPointStyle,
  formatLabel = DEFAULT_FORMAT_LABEL,
}) => {
  const getItem = (dataIndex) =>
    resolveChartMeasureItem({
      dataIndex,
      chartLabels,
      data,
      sourceIndices,
      formatLabel,
    });

  return {
    pointRadius: (context) => {
      const selected = getSelectedPointStyle?.(context.dataIndex);
      if (selected) return selected.radius;
      if (hasMeasureMetadata(getItem(context.dataIndex))) {
        return METADATA_POINT_STYLE.radius;
      }
      return 2;
    },
    pointBackgroundColor: (context) => {
      const selected = getSelectedPointStyle?.(context.dataIndex);
      if (selected) return selected.bg;
      if (hasMeasureMetadata(getItem(context.dataIndex))) {
        return METADATA_POINT_STYLE.bg;
      }
      return undefined;
    },
    pointBorderColor: (context) => {
      const selected = getSelectedPointStyle?.(context.dataIndex);
      if (selected) return selected.border;
      if (hasMeasureMetadata(getItem(context.dataIndex))) {
        return METADATA_POINT_STYLE.border;
      }
      return undefined;
    },
    pointBorderWidth: (context) => {
      const selected = getSelectedPointStyle?.(context.dataIndex);
      if (selected) return 2;
      if (hasMeasureMetadata(getItem(context.dataIndex))) {
        return METADATA_POINT_STYLE.borderWidth;
      }
      return 1;
    },
    pointStyle: (context) => {
      const selected = getSelectedPointStyle?.(context.dataIndex);
      if (selected) return "circle";
      if (hasMeasureMetadata(getItem(context.dataIndex))) {
        return METADATA_POINT_STYLE.pointStyle;
      }
      return "circle";
    },
  };
};

export const buildMetadataLabelsMap = (items = [], formatLabel = DEFAULT_FORMAT_LABEL) => {
  const map = new Map();
  items.forEach((item) => {
    if (!item?.date || !hasMeasureMetadata(item)) return;
    map.set(formatLabel(item.date), item);
  });
  return map;
};

export const buildMetadataPointStylePropsForLabels = (labelSet = [], metadataByLabel = new Map()) => ({
  pointRadius: (context) =>
    metadataByLabel.has(labelSet[context.dataIndex]) ? METADATA_POINT_STYLE.radius : 2,
  pointBackgroundColor: (context) =>
    metadataByLabel.has(labelSet[context.dataIndex])
      ? METADATA_POINT_STYLE.bg
      : undefined,
  pointBorderColor: (context) =>
    metadataByLabel.has(labelSet[context.dataIndex])
      ? METADATA_POINT_STYLE.border
      : undefined,
  pointBorderWidth: (context) =>
    metadataByLabel.has(labelSet[context.dataIndex])
      ? METADATA_POINT_STYLE.borderWidth
      : 1,
  pointStyle: (context) =>
    metadataByLabel.has(labelSet[context.dataIndex])
      ? METADATA_POINT_STYLE.pointStyle
      : "circle",
});

export const createMetadataTooltipCallbacks = ({
  chartLabels,
  data,
  sourceIndices,
  primaryDatasetLabels = ["UMIDADE", "TEMPERATURA", "DENSIDADE", "PH"],
  formatLabel = DEFAULT_FORMAT_LABEL,
  extraCallbacks = {},
}) => ({
  ...extraCallbacks,
  afterBody(context) {
    if (extraCallbacks.afterBody) {
      const existing = extraCallbacks.afterBody(context);
      if (existing?.length) return existing;
    }

    if (!context?.length) return [];

    const primary =
      context.find((item) =>
        primaryDatasetLabels.some(
          (label) =>
            item.dataset.label === label ||
            item.dataset.label?.includes(label)
        )
      ) || context[0];

    const measure = resolveChartMeasureItem({
      dataIndex: primary.dataIndex,
      chartLabels,
      data,
      sourceIndices,
      formatLabel,
    });

    const lines = buildMetadataTooltipLines(measure);
    if (!lines.length) return [];

    return ["—", ...lines];
  },
});
